/**
 * Noise — seeded 3D simplex plus fbm/ridge derivatives, used by the sculpt pattern pass.
 * Worker-safe: no DOM, no globals, no Math.random.
 */

import { Rng } from '../core/Rng.ts';

/** The 12 edge-midpoint gradients of a cube. Integer components keep the dot products exact. */
const GRAD3 = new Int8Array([
  1, 1, 0, -1, 1, 0, 1, -1, 0, -1, -1, 0,
  1, 0, 1, -1, 0, 1, 1, 0, -1, -1, 0, -1,
  0, 1, 1, 0, -1, 1, 0, 1, -1, 0, -1, -1,
]);

/** Skew/unskew factors for 3D: (sqrt(4)-1)/3 and (1 - 1/sqrt(4))/3. */
const F3 = 1 / 3;
const G3 = 1 / 6;

/** Scales the raw kernel sum into [-1,1]; the classic constant for the r^2 = 0.6 support radius. */
const NORM3 = 32;

/** Cost ceiling — the pattern pass runs this per voxel, so runaway octave counts are clamped. */
const MAX_OCTAVES = 8;

export class Simplex3 {
  private readonly perm: Uint8Array;
  private readonly permMod12: Uint8Array;

  constructor(seed: number) {
    const rng = new Rng(seed >>> 0);
    const p = new Uint8Array(256);
    for (let i = 0; i < 256; i++) p[i] = i;
    // Fisher-Yates off the seeded stream: identical table on main thread and in the worker.
    for (let i = 255; i > 0; i--) {
      const j = rng.int(0, i + 1);
      const t = p[i];
      p[i] = p[j];
      p[j] = t;
    }
    // Doubled so the lookups below never need a modulo.
    this.perm = new Uint8Array(512);
    this.permMod12 = new Uint8Array(512);
    for (let i = 0; i < 512; i++) {
      const v = p[i & 255];
      this.perm[i] = v;
      this.permMod12[i] = v % 12;
    }
  }

  noise(x: number, y: number, z: number): number {
    const perm = this.perm;
    const pm12 = this.permMod12;
    const g = GRAD3;

    // Skew the input into the simplex lattice and find the containing cell.
    const s = (x + y + z) * F3;
    const i = Math.floor(x + s);
    const j = Math.floor(y + s);
    const k = Math.floor(z + s);
    const t = (i + j + k) * G3;
    const x0 = x - (i - t);
    const y0 = y - (j - t);
    const z0 = z - (k - t);

    // Rank the three coordinates to pick which of the 6 tetrahedra we are in.
    let i1: number, j1: number, k1: number;
    let i2: number, j2: number, k2: number;
    if (x0 >= y0) {
      if (y0 >= z0) {
        i1 = 1; j1 = 0; k1 = 0; i2 = 1; j2 = 1; k2 = 0;
      } else if (x0 >= z0) {
        i1 = 1; j1 = 0; k1 = 0; i2 = 1; j2 = 0; k2 = 1;
      } else {
        i1 = 0; j1 = 0; k1 = 1; i2 = 1; j2 = 0; k2 = 1;
      }
    } else {
      if (y0 < z0) {
        i1 = 0; j1 = 0; k1 = 1; i2 = 0; j2 = 1; k2 = 1;
      } else if (x0 < z0) {
        i1 = 0; j1 = 1; k1 = 0; i2 = 0; j2 = 1; k2 = 1;
      } else {
        i1 = 0; j1 = 1; k1 = 0; i2 = 1; j2 = 1; k2 = 0;
      }
    }

    const x1 = x0 - i1 + G3;
    const y1 = y0 - j1 + G3;
    const z1 = z0 - k1 + G3;
    const x2 = x0 - i2 + 2 * G3;
    const y2 = y0 - j2 + 2 * G3;
    const z2 = z0 - k2 + 2 * G3;
    const x3 = x0 - 1 + 3 * G3;
    const y3 = y0 - 1 + 3 * G3;
    const z3 = z0 - 1 + 3 * G3;

    const ii = i & 255;
    const jj = j & 255;
    const kk = k & 255;

    let n = 0;

    let t0 = 0.6 - x0 * x0 - y0 * y0 - z0 * z0;
    if (t0 > 0) {
      const gi = pm12[ii + perm[jj + perm[kk]]] * 3;
      t0 *= t0;
      n += t0 * t0 * (g[gi] * x0 + g[gi + 1] * y0 + g[gi + 2] * z0);
    }
    let t1 = 0.6 - x1 * x1 - y1 * y1 - z1 * z1;
    if (t1 > 0) {
      const gi = pm12[ii + i1 + perm[jj + j1 + perm[kk + k1]]] * 3;
      t1 *= t1;
      n += t1 * t1 * (g[gi] * x1 + g[gi + 1] * y1 + g[gi + 2] * z1);
    }
    let t2 = 0.6 - x2 * x2 - y2 * y2 - z2 * z2;
    if (t2 > 0) {
      const gi = pm12[ii + i2 + perm[jj + j2 + perm[kk + k2]]] * 3;
      t2 *= t2;
      n += t2 * t2 * (g[gi] * x2 + g[gi + 1] * y2 + g[gi + 2] * z2);
    }
    let t3 = 0.6 - x3 * x3 - y3 * y3 - z3 * z3;
    if (t3 > 0) {
      const gi = pm12[ii + 1 + perm[jj + 1 + perm[kk + 1]]] * 3;
      t3 *= t3;
      n += t3 * t3 * (g[gi] * x3 + g[gi + 1] * y3 + g[gi + 2] * z3);
    }

    n *= NORM3;
    return n < -1 ? -1 : n > 1 ? 1 : n;
  }

  fbm(x: number, y: number, z: number, octaves = 4, lacunarity = 2, gain = 0.5): number {
    const oct = Math.max(1, Math.min(MAX_OCTAVES, octaves | 0));
    let amp = 1;
    let freq = 1;
    let sum = 0;
    let norm = 0;
    for (let o = 0; o < oct; o++) {
      sum += amp * this.noise(x * freq, y * freq, z * freq);
      norm += amp;
      amp *= gain;
      freq *= lacunarity;
    }
    // Normalised so the result stays in [-1,1] whatever the gain — thresholds in the
    // pattern pass are then stable across octave counts.
    return norm > 0 ? sum / norm : 0;
  }

  ridge(x: number, y: number, z: number, octaves = 4): number {
    const oct = Math.max(1, Math.min(MAX_OCTAVES, octaves | 0));
    let amp = 1;
    let freq = 1;
    let sum = 0;
    let norm = 0;
    // Musgrave-style octave coupling: each octave is masked by the previous one, which is
    // what turns the |noise| creases into closed rings rather than a wash of veins.
    let weight = 1;
    for (let o = 0; o < oct; o++) {
      let v = 1 - Math.abs(this.noise(x * freq, y * freq, z * freq));
      v *= v;
      v *= weight;
      weight = Math.min(1, v * 2);
      sum += amp * v;
      norm += amp;
      amp *= 0.5;
      freq *= 2;
    }
    const r = norm > 0 ? sum / norm : 0;
    return r < 0 ? 0 : r > 1 ? 1 : r;
  }
}
